import React, { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, AcademicCapIcon, ArrowDownTrayIcon, FunnelIcon } from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import StatCard from '../../components/StatCard';
import api from '../../utils/api';

const C = { teal:'#00B4D8', success:'#10B981', dark:'#1F2937', grey:'#6B7280', border:'#E5E7EB', warn:'#F59E0B', purple:'#8B5CF6', error:'#EF4444' };
const STATUS_COLOR = { active: C.success, trial: C.warn, inactive: C.grey, pending: '#2563EB' };

export default function SuperAdminStudents() {
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterPartner, setFilterPartner] = useState('all');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    setLoading(true);
    api.get('/api/superadmin/students')
      .then(res => {
        setStudents(res.data.students || []);
        setStats(res.data.stats || {});
      })
      .catch(e => setError('Failed to load students: ' + (e.response?.data?.error || e.message)))
      .finally(() => setLoading(false));
  }, []);

  const partners = ['all', ...Array.from(new Set(students.map(s => s.partner).filter(Boolean)))];
  const levels = ['all', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
  const statuses = ['all', 'active', 'trial', 'inactive'];

  const filtered = students.filter(s => {
    const q = search.toLowerCase();
    const matchSearch = !search || (s.name||'').toLowerCase().includes(q) || (s.email||'').toLowerCase().includes(q);
    const matchPartner = filterPartner === 'all' || s.partner === filterPartner;
    const matchLevel = filterLevel === 'all' || s.level === filterLevel;
    const matchStatus = filterStatus === 'all' || s.status === filterStatus;
    return matchSearch && matchPartner && matchLevel && matchStatus;
  });

  const totalCount = stats?.total || 0;
  const activeCount = stats?.active || 0;
  const trialCount = stats?.trial || 0;
  const inactiveCount = stats?.inactive || 0;
  const remoteCount = stats?.remote !== undefined ? stats.remote : students.filter(s => !s.learning_mode || s.learning_mode === 'remote').length;
  const physicalCount = stats?.physical !== undefined ? stats.physical : students.filter(s => s.learning_mode === 'physical').length;

  return (
    <Layout role="superadmin">
      {error && (
        <div style={{ background:'#FEE2E2', color:C.error, padding:'12px 16px', borderRadius:8, marginBottom:20 }}>{error}</div>
      )}

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:20, marginBottom:16 }}>
        <StatCard title="Total Students" value={loading ? null : totalCount} icon={AcademicCapIcon} color={C.teal} loading={loading} />
        <StatCard title="Active" value={loading ? null : activeCount} icon={AcademicCapIcon} color={C.success} loading={loading} />
        <StatCard title="On Trial" value={loading ? null : trialCount} icon={AcademicCapIcon} color={C.warn} loading={loading} />
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:20, marginBottom:24 }}>
        <StatCard title="Inactive" value={loading ? null : inactiveCount} icon={AcademicCapIcon} color={C.grey} loading={loading} />
        <StatCard title="🌐 Remote" value={loading ? null : remoteCount} icon={AcademicCapIcon} color='#0369A1' loading={loading} />
        <StatCard title="🏫 Physical" value={loading ? null : physicalCount} icon={AcademicCapIcon} color='#065F46' loading={loading} />
      </div>

      {/* Filters + Table */}
      <div className="card" style={{ overflow:'hidden' }}>
        <div style={{ padding:'20px 24px', borderBottom:`1px solid ${C.border}`, display:'flex', gap:12, flexWrap:'wrap', alignItems:'center' }}>
          <div style={{ position:'relative', flex:1, minWidth:200 }}>
            <MagnifyingGlassIcon style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', width:16, height:16, color:C.grey }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search students..." style={{ width:'100%', padding:'8px 12px 8px 36px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }} />
          </div>
          <FunnelIcon style={{ width:16, height:16, color:C.grey }} />
          {[
            ['Partner', partners, filterPartner, setFilterPartner],
            ['Level', levels, filterLevel, setFilterLevel],
            ['Status', statuses, filterStatus, setFilterStatus]
          ].map(([label, opts, val, set]) => (
            <select key={label} value={val} onChange={e => set(e.target.value)} style={{ padding:'8px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, background:'#fff', cursor:'pointer' }}>
              {opts.map(o => <option key={o} value={o}>{o === 'all' ? `All ${label}s` : o}</option>)}
            </select>
          ))}
          <button style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', background:'#F3F4F6', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', marginLeft:'auto' }}>
            <ArrowDownTrayIcon style={{ width:14, height:14 }} /> Export
          </button>
        </div>

        {loading ? (
          <div style={{ padding:60, textAlign:'center', color:C.grey }}>Loading students...</div>
        ) : filtered.length === 0 && totalCount === 0 ? (
          <div style={{ padding:60, textAlign:'center', color:C.grey }}>
            <div style={{ fontSize:40, marginBottom:12 }}>🎓</div>
            <div style={{ fontSize:18, fontWeight:600, color:'#374151', marginBottom:6 }}>No students yet</div>
            <div style={{ fontSize:14 }}>Students will appear here once partners enroll them.</div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding:40, textAlign:'center', color:C.grey }}>No students match your filters.</div>
        ) : (
          <>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'#F9FAFB' }}>
                {['Student','Partner','Mode','Level','Status','Joined','Lessons'].map(h => (
                  <th key={h} style={{ padding:'12px 20px', textAlign:'left', fontSize:12, fontWeight:600, color:C.grey, textTransform:'uppercase' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {filtered.map((s, i) => (
                  <tr key={s.id} style={{ borderTop:`1px solid ${C.border}`, background: i%2===0 ? '#fff' : '#FAFAFA' }}>
                    <td style={{ padding:'14px 20px' }}>
                      <div style={{ fontWeight:600, fontSize:14 }}>{s.name}</div>
                      <div style={{ fontSize:12, color:C.grey }}>{s.email}</div>
                    </td>
                    <td style={{ padding:'14px 20px', fontSize:13, color:C.grey }}>{s.partner || '—'}</td>
                    <td style={{ padding:'14px 20px' }}>
                      {s.learning_mode === 'physical'
                        ? <span style={{ background:'#D1FAE5', color:'#065F46', fontSize:11, fontWeight:700, padding:'2px 9px', borderRadius:20 }}>🏫 Physical</span>
                        : <span style={{ background:'#E0F2FE', color:'#0369A1', fontSize:11, fontWeight:700, padding:'2px 9px', borderRadius:20 }}>🌐 Remote</span>
                      }
                    </td>
                    <td style={{ padding:'14px 20px' }}>
                      {s.level ? (
                        <span style={{ padding:'2px 10px', borderRadius:20, fontSize:12, fontWeight:700,
                          background: (s.level||'').startsWith('C') ? '#EDE9FE' : (s.level||'').startsWith('B') ? '#DBEAFE' : '#D1FAE5',
                          color: (s.level||'').startsWith('C') ? C.purple : (s.level||'').startsWith('B') ? '#2563EB' : C.success }}>
                          {s.level}
                        </span>
                      ) : <span style={{ color:C.grey }}>—</span>}
                    </td>
                    <td style={{ padding:'14px 20px' }}>
                      <span style={{ padding:'2px 10px', borderRadius:20, fontSize:12, fontWeight:600,
                        background: s.status==='active' ? '#D1FAE5' : s.status==='trial' ? '#FEF3C7' : '#F3F4F6',
                        color: STATUS_COLOR[s.status] || C.grey }}>
                        {s.status || 'unknown'}
                      </span>
                    </td>
                    <td style={{ padding:'14px 20px', fontSize:13, color:C.grey }}>{s.joined ? new Date(s.joined).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'}</td>
                    <td style={{ padding:'14px 20px', fontSize:13, fontWeight:600 }}>{s.lesson_count ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ padding:'14px 24px', borderTop:`1px solid ${C.border}`, fontSize:13, color:C.grey }}>
              Showing {filtered.length} of {totalCount} students
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
